import { View, Text, Pressable, ScrollView } from 'react-native';
import { useState, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { trpc } from '../../lib/trpc';
import {
  Card,
  LoadingSpinner,
  ErrorState,
  PageContainer,
  PageHeader,
  Button,
  Badge,
  useConfirmationDialog,
  WebHead,
} from '../../components';
import { useColors } from '../../lib/colors';

type Rule = {
  id: string;
  merchantName: string | null;
  nameContains: string | null;
  amountMin: number | null;
  amountMax: number | null;
  accountId: string | null;
  accountName: string | null;
  newCategoryId: number;
  categoryName: string;
  priority: number;
  isActive: boolean;
};

function ruleSummary(rule: Rule): string {
  const parts: string[] = [];
  if (rule.merchantName) parts.push(`Merchant: ${rule.merchantName}`);
  if (rule.nameContains) parts.push(`Description contains: "${rule.nameContains}"`);
  if (rule.amountMin != null && rule.amountMax != null) {
    parts.push(`$${rule.amountMin} – $${rule.amountMax}`);
  } else if (rule.amountMin != null) {
    parts.push(`>= $${rule.amountMin}`);
  } else if (rule.amountMax != null) {
    parts.push(`<= $${rule.amountMax}`);
  }
  if (rule.accountName) parts.push(`Account: ${rule.accountName}`);
  return parts.length > 0 ? parts.join(' + ') : 'All transactions';
}

export default function CategoryRulesScreen() {
  const c = useColors();
  const router = useRouter();
  const utils = trpc.useUtils();
  const rulesQ = trpc.categoryRules.list.useQuery();
  const updateMutation = trpc.categoryRules.update.useMutation();
  const deleteMutation = trpc.categoryRules.delete.useMutation();
  const { confirmDestructive } = useConfirmationDialog();

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleToggleActive = useCallback(
    async (rule: Rule) => {
      await updateMutation.mutateAsync({ id: rule.id, isActive: !rule.isActive });
      utils.categoryRules.list.invalidate();
    },
    [updateMutation, utils],
  );

  const handleDelete = useCallback(
    async (rule: Rule) => {
      const confirmed = await confirmDestructive({
        title: 'Delete rule?',
        message: `Remove the rule "${ruleSummary(rule)}"?`,
        confirmLabel: 'Delete',
      });
      if (!confirmed) return;

      const doDelete = async () => {
        setDeletingId(rule.id);
        await deleteMutation.mutateAsync({ id: rule.id });
        utils.categoryRules.list.invalidate();
        setDeletingId(null);
      };

      await doDelete();
    },
    [confirmDestructive, deleteMutation, utils],
  );

  if (rulesQ.isLoading) return <LoadingSpinner />;
  if (rulesQ.error) {
    return (
      <ErrorState
        error={rulesQ.error}
        screen="category-rules"
        metadata={{ failedQueries: 'categoryRules.list' }}
        onRetry={rulesQ.refetch}
      />
    );
  }

  const rules = (rulesQ.data ?? []) as Rule[];

  return (
    <>
      <WebHead><title>Category Rules – Per Diem</title></WebHead>
      <ScrollView className="flex-1 bg-canvas dark:bg-canvas-dark">
        <PageContainer>
          <PageHeader title="Category Rules" closeTo="/(app)/transactions" />

          <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Rules automatically categorize transactions during sync. Higher priority rules are evaluated first.
            Create rules by recategorizing transactions and selecting &ldquo;All from [merchant]&rdquo;, or by using
            advanced filters on the Transactions screen.
          </Text>

          {rules.length === 0 ? (
            <Card className="items-center py-8">
              <Ionicons name="layers-outline" size={32} color={c.textSecondary} />
              <Text className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                No rules yet
              </Text>
              <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center px-8">
                Recategorize a transaction and choose &ldquo;Apply to all&rdquo; to create your first rule.
              </Text>
            </Card>
          ) : (
            <Card className="p-0 overflow-hidden">
              {rules.map((rule, i) => (
                <View
                  key={rule.id}
                  className={`px-4 py-3 ${i > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}`}
                >
                  <View className="flex-row items-start justify-between">
                    <View className="flex-1 pr-3">
                      <Text className="text-sm font-medium text-gray-800 dark:text-gray-200" numberOfLines={2}>
                        {ruleSummary(rule)}
                      </Text>
                      <View className="flex-row items-center gap-2 mt-1">
                        <Ionicons name="arrow-forward" size={12} color={c.textSecondary} />
                        <Badge label={rule.categoryName} variant="gray" />
                        {!rule.isActive && <Badge label="Paused" variant="yellow" />}
                      </View>
                    </View>

                    <View className="flex-row items-center gap-1">
                      <Pressable
                        onPress={() => handleToggleActive(rule)}
                        className="p-2"
                        hitSlop={8}
                      >
                        <Ionicons
                          name={rule.isActive ? 'pause-circle-outline' : 'play-circle-outline'}
                          size={20}
                          color={rule.isActive ? c.textSecondary : c.primary}
                        />
                      </Pressable>
                      <Pressable
                        onPress={() => handleDelete(rule)}
                        className="p-2"
                        hitSlop={8}
                        disabled={deletingId === rule.id}
                      >
                        <Ionicons
                          name="trash-outline"
                          size={18}
                          color={deletingId === rule.id ? c.textSecondary : '#ef4444'}
                        />
                      </Pressable>
                    </View>
                  </View>
                </View>
              ))}
            </Card>
          )}
        </PageContainer>
      </ScrollView>
    </>
  );
}
